import { LIVE_FRAME_MS } from '../constants.js';
import { averageNumbers } from '../util/math.js';
import { teamPlayersAtPosition } from '../../team-roster.js';

export function runPixelsPerSecondForSpeed(speedRating: number): number {
  const normalized = Math.max(45, Math.min(99, speedRating || 75));
  return 104 + (normalized - 45) * 0.8;
}

export function passPixelsPerSecondForSpeed(speedRating: number): number {
  const normalized = Math.max(45, Math.min(99, speedRating || 75));
  // Pass plays share the same on-screen tempo as runs; only the route shapes
  // and ball flight should make them feel different.
  return 118 + (normalized - 45) * 0.88;
}

export function playerPixelsPerFrame(speedRating: number, family: string = 'pass', scale: number = 1): number {
  const pixelsPerSecond = family === 'run'
    ? runPixelsPerSecondForSpeed(speedRating)
    : passPixelsPerSecondForSpeed(speedRating);
  const softener = family === 'run' ? 0.92 : 0.84;
  return Math.max(1.5, pixelsPerSecond * (LIVE_FRAME_MS / 1000) * softener * scale);
}

export function liveTempoForSpeed(speedRating: number): number {
  const normalized = Math.max(45, Math.min(99, speedRating || 75));
  return Math.max(0.94, Math.min(1.04, 1 + (normalized - 75) / 420));
}

function topPlayers(team: any, position: string, count: number = 1): any[] {
  return teamPlayersAtPosition(team, position, count);
}

function averagePlayerSpeed(players: any[], fallback: number = 72): number {
  return averageNumbers(
    players.map((p: any) => p?.ratings?.speed).filter((v: any) => Number.isFinite(v)),
    fallback
  );
}

export function buildTeamSpeedProfile(team: any): Record<string, number> {
  const profile: Record<string, number> = {
    QB: averagePlayerSpeed(topPlayers(team, 'QB', 1), 66),
    RB: averagePlayerSpeed(topPlayers(team, 'RB', 1), 84),
    FB: averagePlayerSpeed(topPlayers(team, 'FB', 1), 70),
    WR: averagePlayerSpeed(topPlayers(team, 'WR', 3), 88),
    TE: averagePlayerSpeed(topPlayers(team, 'TE', 2), 76),
    OT: averagePlayerSpeed(topPlayers(team, 'OT', 2), 58),
    OG: averagePlayerSpeed(topPlayers(team, 'OG', 2), 54),
    C: averagePlayerSpeed(topPlayers(team, 'C', 1), 52),
    DE: averagePlayerSpeed(topPlayers(team, 'DE', 2), 76),
    DT: averagePlayerSpeed(topPlayers(team, 'DT', 2), 62),
    LB: averagePlayerSpeed(topPlayers(team, 'LB', 3), 78),
    CB: averagePlayerSpeed(topPlayers(team, 'CB', 2), 88),
    FS: averagePlayerSpeed(topPlayers(team, 'S', 1), 84),
    SS: averagePlayerSpeed(topPlayers(team, 'S', 2).slice(1), 82),
    K: averagePlayerSpeed(topPlayers(team, 'K', 1), 56),
    P: averagePlayerSpeed(topPlayers(team, 'P', 1), 58),
  };
  profile.SAFETY = averageNumbers([profile.FS, profile.SS], 83);
  profile.RETURNER = averageNumbers([profile.WR, profile.RB], 86);
  profile.COVER = averageNumbers([profile.CB, profile.FS, profile.SS], 85);
  profile.PROTECT = averageNumbers([profile.OG, profile.OT], 56);
  profile.RUSH = averageNumbers([profile.DE, profile.LB], 77);
  profile.FRONT = averageNumbers([profile.DT, profile.DE], 69);
  profile.UPBACK = averageNumbers([profile.FB, profile.LB], 72);
  profile.GUNNER = averageNumbers([profile.WR, profile.CB], 86);
  return profile;
}

export function teamSpeedProfile(team: any): Record<string, number> {
  if (!team._speedProfile) {
    team._speedProfile = buildTeamSpeedProfile(team);
  }
  return team._speedProfile;
}

export function speedRatingForRole(team: any, role: string): number {
  const profile = teamSpeedProfile(team);
  if (role === 'QB') return profile.QB;
  if (role === 'RB') return profile.RB;
  if (role === 'TE') return profile.TE;
  if (role === 'C') return profile.C;
  if (role === 'LT' || role === 'RT') return profile.OT;
  if (role === 'LG' || role === 'RG') return profile.OG;
  if (role === 'WR1' || role === 'WR2' || role === 'SLOT') return profile.WR;
  if (role === 'DE1' || role === 'DE2') return profile.DE;
  if (role === 'DT1' || role === 'DT2') return profile.DT;
  if (role === 'LB1' || role === 'LB2' || role === 'LB3') return profile.LB;
  if (role === 'CB1' || role === 'CB2') return profile.CB;
  if (role === 'FS') return profile.FS;
  if (role === 'SS') return profile.SS;
  if (role === 'K') return profile.K;
  if (role === 'P') return profile.P;
  if (role === 'HOLDER') return profile.K;
  if (role === 'RETURNER') return profile.RETURNER;
  if (role === 'SAFETY' || role === 'SAFETY1' || role === 'SAFETY2') return profile.SAFETY;
  if (role.startsWith('GUNNER')) return profile.GUNNER;
  if (role.startsWith('COVER')) return profile.COVER;
  if (role.startsWith('FRONT')) return profile.FRONT;
  if (role.startsWith('WALL')) return profile.WALL || 56;
  if (role.startsWith('PROTECT') || role.startsWith('BLOCK')) return profile.PROTECT;
  if (role.startsWith('RUSH')) return profile.RUSH;
  if (role.startsWith('EDGE')) return profile.RUSH;
  if (role.startsWith('LEAPER')) return profile.DT;
  if (role.startsWith('CONTAIN')) return profile.LB;
  return 75;
}

export function addLiveTempo(formation: { offense: any[]; defense: any[] }, offense: any, defense: any): { offense: any[]; defense: any[] } {
  return {
    offense: formation.offense.map((player) => {
      const sr = speedRatingForRole(offense, player.role);
      return { ...player, speedRating: sr, liveTempo: liveTempoForSpeed(sr) };
    }),
    defense: formation.defense.map((player) => {
      const sr = speedRatingForRole(defense, player.role);
      return { ...player, speedRating: sr, liveTempo: liveTempoForSpeed(sr) };
    }),
  };
}
